/**
 * @fileoverview 用户相关 API 服务
 * @description 封装用户信息、余额、投注记录、充提历史等接口
 * MVC 架构：Service 层，负责用户数据 HTTP 通信（需要认证）
 * @module services/userService
 */

import { get, post, type BackendResponse } from './api';

// ============================================================
// 类型定义
// ============================================================

/**
 * 后端用户信息原始结构（对应 user 数据库表 + balance 字段）
 */
export interface BackendUserInfo {
  /** 钱包地址（主键） */
  account: string;
  /** 用户 GUID */
  guid: number;
  /** 链类型 */
  chain: string;
  /** 账户名称 */
  accountName: string;
  /** 推荐人 ID */
  referrerId?: number;
  /** 推荐码 */
  referralCode?: string;
  /** 邮箱（可选） */
  email?: string;
  /** 余额（USDT，由 userInfo 接口额外附加） */
  balance?: number;
  /** 创建时间 */
  createdAt?: string;
}

/**
 * 投注记录原始结构（对应 investRecord 数据库表）
 */
export interface BackendInvestRecord {
  id: number;
  marketId: number;
  /** 对应的 marketRound.id（用于关联 marketOption.roundId） */
  roundId?: number;
  guid: number;
  /** 投注金额（最小单位） */
  amount: number;
  /** 投注方向：1=yes, 0=no */
  event: number;
  /** 状态：0=投注中, 1=已赢（PROFIT 记录）；输了不会更新此字段 */
  status: number;
  /** 链上交易哈希 */
  txHash?: string;
  createdAt?: string;
}

/**
 * 市场持仓原始结构（对应 marketOption 数据库表）
 */
export interface BackendMarketOption {
  /** 对应 marketRound.id（联合主键之一） */
  roundId: number;
  marketId: number;
  guid: number;
  /** Yes 方向投注总额 */
  yesAmount: number;
  /** No 方向投注总额 */
  noAmount: number;
  /** Yes 方向累计时间权重（用于计算我的返还率） */
  yesWeight?: number;
  /** No 方向累计时间权重（用于计算我的返还率） */
  noWeight?: number;
  /**
   * 该轮的总额/总权重（后端 marketOption JOIN marketRounds 实时下发）。
   * 与本行的 my 权重/金额同源同刻，用它算返还率可避免与 marketInfo 缓存不一致导致的"刷新跳变/虚高"。
   * 老后端不返回时为 undefined，前端回退到 marketInfo 快照。
   */
  roundYesAmount?: number;
  roundNoAmount?: number;
  roundYesWeight?: number;
  roundNoWeight?: number;
  /** 状态：0=投注中(INVESTING), 1=已赢(WIN), 2=已输(LOSS), 3=平局(TIE) */
  status: number;
  /** 本轮结果：-1=未知(UNKNOWN), 0=NO赢, 1=YES赢 */
  result: number;
  /** 最终收益（赢了才有值） */
  finalProfit: number;
  /** 平台手续费（赢了才有值，从赢家收益中按 taxRate 扣除） */
  finalTax?: number;
  /** 基准价格（本轮开盘时设定的参考价，来自 marketRound.targetPrice） */
  targetPrice?: number | null;
  /** 结算价格（本轮结算时的实际收盘价，来自 marketRound.finalPrice）未结算时为 0 或 null */
  finalPrice?: number | null;
}

/**
 * 充值记录原始结构（对应 evmPayment 数据库表）
 */
export interface BackendDepositRecord {
  txHash: string;
  tokenAddress: string;
  amount: string;
  account: string;
  to: string;
  timestamp: number;
  blockNumber: number;
  chain: string;
  memoType?: number;
  memoValue?: string;
}

/**
 * 提现记录原始结构（对应 assetWithdraw 数据库表）
 */
export interface BackendWithdrawRecord {
  id: number;
  guid: number;
  amount: number;
  walletAddress?: string;
  chainType?: string;
  transferHash?: string;
  /** 状态：0=待审核(WITHDRAWING), 1=转账中(TRANSFERING), 2=已审核(APPOVED), 3=已完成(COMPLETE) */
  status: number;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * 提现请求参数
 */
export interface WithdrawRequest {
  amount: number;
}

/**
 * 领取奖励请求参数
 */
export interface ClaimRequest {
  marketId: number;
}

// ============================================================
// API 函数
// ============================================================

/**
 * 获取当前登录用户信息（含余额）
 * @returns 用户信息（含 balance 字段）
 */
export async function fetchUserInfo(): Promise<BackendResponse<BackendUserInfo>> {
  return get<BackendUserInfo>('/api/v1/user/userInfo', undefined, true);
}

/**
 * 获取用户余额
 * @returns 余额数值（USDT）
 */
export async function fetchUserBalance(): Promise<BackendResponse<number>> {
  return get<number>('/api/v1/user/balance', undefined, true);
}

/**
 * /api/v1/user/referralCode 接口返回结构
 * 包含邀请码、邀请链接、分享链接及社媒地址
 */
export interface ReferralCodeInfo {
  /** 邀请码（如 DGdok0hO） */
  referralCode: string;
  /** TG 邀请深链接：https://t.me/Bot/App?startapp=CODE */
  inviteLink: string;
  /** TG 分享链接：https://t.me/share/url?url=...&text=... */
  shareUrl: string;
  /** TG 群组链接 */
  group: string;
  /** Twitter 链接 */
  twitter: string;
  /** TG 频道链接 */
  tgChannel: string;
}

/**
 * 获取或生成推荐码及相关链接
 * @returns ReferralCodeInfo（含邀请链接、分享链接、社媒链接）
 */
export async function fetchReferralCode(): Promise<BackendResponse<ReferralCodeInfo>> {
  return post<ReferralCodeInfo>('/api/v1/user/referralCode', undefined, true);
}

/**
 * 获取用户投注记录列表
 * @param page - 页码，从 0 开始
 * @returns 投注记录数组
 */
export async function fetchInvestList(
  page = 0
): Promise<BackendResponse<BackendInvestRecord[]>> {
  return get<BackendInvestRecord[]>('/api/v1/user/investList', { page }, true);
}

/**
 * 获取指定市场的用户投注记录（按 marketId 过滤，包含所有轮次）
 * @param marketId - 市场 ID
 * @returns 该市场的投注记录数组
 */
export async function fetchMarketInvestList(
  marketId: string | number
): Promise<BackendResponse<BackendInvestRecord[]>> {
  return get<BackendInvestRecord[]>('/api/v1/user/marketInvestList', { marketId: Number(marketId) }, true);
}
/**
 * 获取用户在指定市场的持仓
 * @param marketId - 市场 ID
 * @returns 市场持仓数据
 */
export async function fetchMarketOption(
  marketId: string | number
): Promise<BackendResponse<BackendMarketOption[]>> {
  return get<BackendMarketOption[]>(
    '/api/v1/user/marketOption',
    { marketId: Number(marketId) },
    true
  );
}

/**
 * 批量获取多个市场的 marketOption（用于判断投注是否已结算）
 * @param marketIds - 市场 ID 数组（去重后）
 * @returns roundId → status 映射表
 */
export async function fetchMarketOptionStatusMap(
  marketIds: number[]
): Promise<Map<number, number>> {
  const map = new Map<number, number>();
  await Promise.all(
    marketIds.map(async (marketId) => {
      try {
        const resp = await fetchMarketOption(marketId);
        if (resp.status === 0 && Array.isArray(resp.data)) {
          for (const opt of resp.data) {
            map.set(opt.roundId, opt.status);
          }
        }
      } catch {
        // 静默失败
      }
    })
  );
  return map;
}

/**
 * 获取充值历史记录
 * @returns 充值记录数组
 */
export async function fetchDepositHistory(): Promise<BackendResponse<BackendDepositRecord[]>> {
  return get<BackendDepositRecord[]>('/api/v1/user/depositHistory', undefined, true);
}

/**
 * 获取提现历史记录
 * @returns 提现记录数组
 */
export async function fetchWithdrawHistory(): Promise<BackendResponse<BackendWithdrawRecord[]>> {
  return get<BackendWithdrawRecord[]>('/api/v1/user/withdrawHistory', undefined, true);
}

/**
 * 发起提现申请
 * @param amount - 提现金额（USDT）
 * @returns 提现记录
 */
export async function requestWithdraw(
  amount: number,
  address?: string,
  chainType?: string
): Promise<BackendResponse<BackendWithdrawRecord>> {
  return post<BackendWithdrawRecord>('/api/v1/user/withdraw', {
    amount,
    ...(address ? { walletAddress: address } : {}),
    ...(chainType ? { chainType } : {}),
  }, true);
}

/**
 * 领取市场结算奖励
 * @param marketId - 市场 ID
 * @returns 领取结果
 */
export async function claimMarketReward(
  marketId: number
): Promise<BackendResponse<unknown>> {
  return post<unknown>('/api/v1/user/claim', { marketId }, true);
}

/**
 * 获取排名信息
 * @returns 排名相关数据
 */
export async function fetchRankInfo(): Promise<BackendResponse<{
  claimedReward: number;
  claimableRankReward: number;
}>> {
  return get<{ claimedReward: number; claimableRankReward: number }>(
    '/api/v1/user/getRankInfo',
    undefined,
    true
  );
}

/**
 * 邀请信息返回结构
 */
export interface ReferrerInfo {
  referralCode: string;
  inviteCount: number;
  referrerPointTotal: number;
  totalPoint: number;
}

/**
 * 获取推荐信息
 * @returns 推荐相关统计数据（邀请码、邀请人数、积分等）
 */
export async function fetchReferrerInfo(): Promise<BackendResponse<ReferrerInfo>> {
  return get<ReferrerInfo>(
    '/api/v1/user/getReferrerInfo',
    undefined,
    true
  );
}

/**
 * 邀请历史记录条目
 */
export interface ReferrerHistoryItem {
  id: number;
  playerId: number;
  accountName: string;
  point: number;
  createdAt: string;
}

/**
 * 获取邀请历史记录（被邀请人的积分奖励记录）
 * @param page - 页码，从 0 开始
 * @param pageSize - 每页条数，默认 20
 */
export async function fetchReferrerHistory(
  page = 0,
  pageSize = 20
): Promise<BackendResponse<ReferrerHistoryItem[]>> {
  return get<ReferrerHistoryItem[]>(
    '/api/v1/user/getReferrerHistory',
    { page, pageSize },
    true
  );
}

/**
 * 获取排行榜（积分排名）
 * @returns 排行榜用户列表
 */
export async function fetchPointRank(): Promise<BackendResponse<BackendUserInfo[]>> {
  return get<BackendUserInfo[]>('/api/v1/public/pointRank');
}

/**
 * 收益排行榜条目（对应 /api/v1/public/profitRank 返回结构）
 */
export interface BackendProfitRankEntry {
  guid: number;
  account: string;
  accountName: string;
  /** 累计收益（USDT，已扣税） */
  totalProfit: number;
}

/**
 * 获取收益排行榜（Top 100，按 totalProfit 降序）
 * @returns 排行榜数组
 */
export async function fetchProfitRank(): Promise<BackendResponse<BackendProfitRankEntry[]>> {
  return get<BackendProfitRankEntry[]>('/api/v1/public/profitRank');
}

/**
 * 用户统计数据（对应 /api/v1/user/profile 返回结构）
 */
export interface BackendUserProfileStats {
  /** 总盈亏（赢-亏，USDT） */
  totalPnl: number;
  /** 胜率（0~1） */
  winRate: number;
  /** 参与过的市场数 */
  marketsJoined: number;
  /** 总竞猜额（USDT） */
  totalVolume: number;
  /** 胜场数 */
  winCount: number;
  /** 负场数 */
  lossCount: number;
}

/**
 * 获取用户个人统计数据（总盈亏、胜率、参与市场数、总交易量）
 * @returns 用户统计数据
 */
export async function fetchUserProfileStats(): Promise<BackendResponse<BackendUserProfileStats>> {
  return get<BackendUserProfileStats>('/api/v1/user/profile', undefined, true);
}

/**
 * 上报用户界面语言到服务器（写入 users.languageCode）
 * 服务器市场列表接口会优先读取此字段来返回对应语言的市场内容
 * @param languageCode - 语言代码，如 "zh-hant"、"en"
 */
export async function updateLanguageCode(languageCode: string): Promise<BackendResponse<{ languageCode: string }>> {
  return post<{ languageCode: string }>('/api/v1/user/languageCode', { languageCode }, true);
}
